import {
  JSX,
  createContext,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  on,
  onCleanup,
  useContext,
} from 'solid-js';
import { useConnectProject } from '@trimble/connect-project-providers';
import { useApiServices } from './ApiServiceProvider';
import { PERSISTENCE_KEY } from './constants/make-persisted-constants';
import {
  AlignmentSettings,
  DEFAULT_ALIGNMENT_SETTINGS,
  FormattingSettings,
  SlopeMeasurementSettings,
} from '@trimble/types';
import {
  DEFAULT_FORMATTING_SETTINGS,
  validateFormattingSettings,
} from '@trimble/utilities';
import { makeMergedPersisted, useLogger } from '@trimble/solidjs-utils';
import { DEFAULT_SLOPE_MEASUREMENT_SETTINGS } from '@data-access/civil/worksos/project-service';
import { TCPSUnitSettingsService } from '@data-access/connect/tc-tcps';
import { asError } from '@trimble/logging';

export function ProjectSettingsProvider(props: {
  children: JSX.Element;
}): JSX.Element {
  const logger = useLogger().getLogger('ProjectSettingsProvider');

  const { selectedRegion, selectedProject, selectedTenantTrn } =
    useConnectProject();
  const { getTcpsRegionClient, alignmentApi, scanDataApi } = useApiServices();

  const [isScanDataRegionSupported] = createResource(
    selectedRegion,
    async (region) => {
      return scanDataApi.isScanDataRegionSupported(region);
    }
  );

  const [regionClient] = createResource(selectedRegion, async (region) => {
    return await getTcpsRegionClient(region);
  });

  const settingsService = createMemo(() => {
    const client = regionClient();
    if (selectedProject()?.id && client) {
      return new TCPSUnitSettingsService(client);
    } else {
      return undefined;
    }
  });

  const [formattingSettings, setFormattingSettings] =
    createSignal<FormattingSettings>(DEFAULT_FORMATTING_SETTINGS);

  const [slopeMeasurementSettings, setSlopeMeasurementSettings] =
    makeMergedPersisted(
      // eslint-disable-next-line solid/reactivity
      createSignal<SlopeMeasurementSettings>(
        DEFAULT_SLOPE_MEASUREMENT_SETTINGS
      ),
      { name: PERSISTENCE_KEY.SlopeMeasurementSettings }
    );

  // Alignment settings are persisted per project via the Alignments API
  // under the key "NumarkAlignmentSettings".
  const [alignmentSettings, setAlignmentSettingsRaw] =
    createSignal<AlignmentSettings>({ ...DEFAULT_ALIGNMENT_SETTINGS });

  const [alignmentRefreshVersion, setAlignmentRefreshVersion] = createSignal(0);

  const SAVE_DEBOUNCE_MS = 1000;
  let saveTimer: ReturnType<typeof setTimeout> | undefined;

  const clearSaveTimer = (): void => {
    clearTimeout(saveTimer);
    saveTimer = undefined;
  };

  const flushPendingSave = (): void => {
    if (!saveTimer) return;

    const tenantTrn = selectedTenantTrn();
    const settings = alignmentSettings();
    clearSaveTimer();

    if (tenantTrn) {
      alignmentApi.saveAlignmentSettings(tenantTrn, settings).catch((error) => {
        logger.error('Failed to save alignment settings', asError(error));
      });
    }
  };

  onCleanup(clearSaveTimer);

  createEffect(
    on(selectedTenantTrn, (tenantTrn, prevTenantTrn) => {
      if (tenantTrn === prevTenantTrn) return;

      clearSaveTimer();

      if (!tenantTrn) return;

      // Reset immediately on tenant changes to avoid showing stale settings
      // while region-support check and API requests are still resolving.
      setAlignmentSettingsRaw({ ...DEFAULT_ALIGNMENT_SETTINGS });
    })
  );

  createEffect(() => {
    const tenantTrn = selectedTenantTrn();

    const isRegionExplicitlyUnsupported =
      isScanDataRegionSupported.state === 'ready' &&
      isScanDataRegionSupported() === false;
    const isRegionSupportCheckResolved =
      isScanDataRegionSupported.state === 'ready' ||
      isScanDataRegionSupported.state === 'errored';

    if (!tenantTrn || isRegionExplicitlyUnsupported) {
      // No active tenant (or unsupported region): discard pending saves tied to old context.
      clearSaveTimer();
      return;
    }

    if (!isRegionSupportCheckResolved) return;

    const defaults = { ...DEFAULT_ALIGNMENT_SETTINGS };

    let cancelled = false;
    onCleanup(() => {
      cancelled = true;
    });

    alignmentApi
      .getAlignmentSettings(tenantTrn)
      .then(async (stored) => {
        if (cancelled) return;
        // Preserve in-flight local edits while a debounced save is pending.
        if (saveTimer) return;

        if (stored) {
          // Merge with defaults so that newly-added fields get their default values.
          setAlignmentSettingsRaw({
            ...DEFAULT_ALIGNMENT_SETTINGS,
            ...stored,
          });
        } else {
          // Settings not found or invalid — use defaults and persist them.
          await alignmentApi.saveAlignmentSettings(tenantTrn, defaults);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        logger.error(
          'Failed to load or persist alignment settings',
          asError(error)
        );
      });
  });

  createEffect(() => {
    const service = settingsService();
    const projectId = selectedProject()?.id;
    const region = selectedRegion();

    if (!(service === undefined || projectId === undefined)) {
      service
        .getUnitSettings(projectId)
        .then((formattingSettings) => {
          if (formattingSettings instanceof Error) {
            logger.error(`Failed to get formatting settings`, {
              projectId,
              formattingSettings,
            });
            return;
          }

          // If no formatting settings, set to defaults
          if (!formattingSettings) {
            logger.error('No formatting settings found, using default', {
              projectId,
            });
            return;
          }

          // Validate the formatting settings
          const validationResult = validateFormattingSettings(
            formattingSettings,
            region?.isMaster ?? true
          );

          // Check the validation result
          const settings = validationResult.settings;
          if (!settings) {
            logger.error(`Invalid formatting settings`, {
              projectId,
              formattingSettings,
            });
            return;
          }

          setFormattingSettings(settings);
        })
        .catch((error) => {
          logger.error(
            'Error fetching project settings',
            { projectId, region },
            asError(error)
          );
        });
    }
  });

  function updateSlopeMeasurementSettings(
    settings: SlopeMeasurementSettings
  ): void {
    setSlopeMeasurementSettings(settings);
  }

  function updateAlignmentSettings(settings: AlignmentSettings): void {
    setAlignmentSettingsRaw(settings);
    const tenantTrn = selectedTenantTrn();
    if (!tenantTrn) return;

    clearSaveTimer();
    saveTimer = setTimeout(() => {
      alignmentApi.saveAlignmentSettings(tenantTrn, settings).catch((error) => {
        logger.error('Failed to save alignment settings', asError(error));
      });
    }, SAVE_DEBOUNCE_MS);
  }

  function refreshAlignmentData(): void {
    const tenantTrn = selectedTenantTrn();
    if (!tenantTrn) return;

    if (saveTimer) {
      flushPendingSave();
      setAlignmentRefreshVersion((v) => v + 1);
      return;
    }

    alignmentApi
      .getAlignmentSettings(tenantTrn)
      .then((stored) => {
        if (stored) {
          setAlignmentSettingsRaw({
            ...DEFAULT_ALIGNMENT_SETTINGS,
            ...stored,
          });
        }
      })
      .catch((error) => {
        logger.error('Failed to refresh alignment settings', asError(error));
      });
  }

  async function updateFormattingSettings(
    formattingSettings: FormattingSettings
  ): Promise<boolean> {
    const service = settingsService();
    const projectId = selectedProject()?.id;

    if (!(service === undefined || projectId === undefined)) {
      const result = await service.updateUnitSettings(
        projectId,
        formattingSettings
      );

      if (result instanceof Error || result !== true) {
        logger.error(
          'Failed to update formatting settings',
          { projectId, formattingSettings },
          result instanceof Error ? result : undefined
        );
        return false;
      }

      setFormattingSettings(formattingSettings);
      return true;
    }
    return false;
  }

  return (
    <ProjectSettingsContext.Provider
      value={{
        formattingSettings,
        updateFormattingSettings,
        slopeMeasurementSettings,
        updateSlopeMeasurementSettings,
        alignmentSettings,
        updateAlignmentSettings,
        alignmentRefreshVersion,
        refreshAlignmentData,
      }}
    >
      {props.children}
    </ProjectSettingsContext.Provider>
  );
}

export interface ProjectSettingsState {
  formattingSettings(): FormattingSettings;
  updateFormattingSettings(
    formattingSettings: FormattingSettings
  ): Promise<boolean>;
  slopeMeasurementSettings(): SlopeMeasurementSettings;
  updateSlopeMeasurementSettings(settings: SlopeMeasurementSettings): void;
  alignmentSettings(): AlignmentSettings;
  updateAlignmentSettings(settings: AlignmentSettings): void;
  alignmentRefreshVersion(): number;
  refreshAlignmentData(): void;
}

const ProjectSettingsContext = createContext<ProjectSettingsState>();

export const useProjectSettings = (): ProjectSettingsState => {
  const context = useContext(ProjectSettingsContext);
  if (!context) {
    throw new Error(
      'useProjectSettings must be used within a ProjectSettingsProvider'
    );
  }
  return context;
};
